import React, { useEffect, useRef, useState } from 'react';
import {
  CoreNavigationbar,
  CoreNavigationbarItem
} from '@komodo-design-system/komodo-components-react';
import SingleUploadContent from './SingleUploadContent';
import { useDispatch, useSelector } from 'react-redux';
import SingleUploadContentFiles from './SingleUploadContentFiles';
import ActionButton from '../BulkUpload/ActionButton';
import './../BulkUpload/BulkUploadContent.scss';
import './SingleUploadContent.scss';
import { singleUploadActions } from "actions/singleUpload";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import ConfirmationAlertBox from "componentsV2/SharedAlertBox/ConfirmationAlertBox/ConfirmationAlertBox";
import submitValidation from "actions/submitValidation";
import { UploadedFile } from '@komodo-design-system/komodo-core-web-components';
import { assetUploadService } from 'api/assetUploadService';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
import { MetadataElement } from './SingleUploadModel';
import { HOME_PAGE } from 'constants/routes';
import { useNavigate } from 'react-router';
import { dropdownOptionsAction } from 'actions/dropdownOptions';
import { DROPDOWN_OPTIONS_RESET } from 'constants/actionTypes';


const SingleUploadMain = () => {
  const {userName} = useSelector((state:any) => state.userTypeReducer?.userType?.data) || {};
  const [activeTab, setActiveTab] = React.useState('Files');
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const singleUploadPayload = useSelector(
    (state: any) => state.singleUpload.payload
  );
  const [prevSingleUploadPayload, setPrevSingleUploadPayload] = useState(
    singleUploadPayload
  );
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [error, setError] = useState({ message: '', isError: false });

  const [asstType, setAssetType] = useState({
    value: '',
    label: ''
  });
  const [alert, setAlert] = useState({
    isOpen: false,
    reason: '',
    message: ''
  });
  const [responseAlert, setResponseAlert] = useState({
    isOpen: false,
    inputProps: {success: false},
    message: ''
  });
  const assetTypes = useSelector(
    (state: any) => state.assetTypeReducer?.assetTypes
  );
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  const prevAssetType = useRef(asstType);


  const uploadFile = (event: CustomEvent<UploadedFile[]>) => {
    if (event.detail.length === 0) {
      return;
    }  
    const detail = event.detail[0];
    const fileName = detail.name;
  
    const modelDefaults = assetTypes?.metadata_collection?.MODEL_DEFAULTS;
    const supportedFileTypes = getSupportedFileTypes(modelDefaults, assetTypeKey);
    const isAllowedFileType = isFileTypeSupported(fileName, supportedFileTypes);

    if (isAllowedFileType && !fileName.trim().includes(' ')) {
      handleValidFile(detail);
    }
    else {
      handleInvalidFile(fileName, modelDefaults, assetTypeKey, isAllowedFileType);
    }
  };
  
  // Helper function to get supported file types
  const getSupportedFileTypes = (modelDefaults: any, assetTypeKey: string | undefined): string[] => {
    const supportedFileTypes = modelDefaults?.[assetTypeKey ?? '']?.allowed_ext ? modelDefaults?.[assetTypeKey ?? '']?.allowed_ext : [];
    if (supportedFileTypes.length === 0) {
      return []; // Return an empty array if no supported file types are defined
    }
    return supportedFileTypes?.split(',').map((type: string) => type.trim().toLowerCase());
  };
  
  // Helper function to check if the file type is supported
  const isFileTypeSupported = (fileName: string, supportedFileTypes: string[]): boolean => {
    const fileExtension = (fileName?.split('.')[1] ?? '').toLowerCase();
    return supportedFileTypes.includes(fileExtension) || supportedFileTypes.length === 0;
  };
  
  // Handle valid file upload
  const handleValidFile = (detail: UploadedFile) => {
    detail.status = 'completed';
    setFiles([detail]);
  };
  
  // Handle invalid file upload
  const handleInvalidFile = (name: string, modelDefaults: any, assetTypeKey: string | undefined, isAllowedFileType:any) => {
    if(!isAllowedFileType){
      setResponseAlert({
        isOpen: true,
        inputProps: { success: false },
        message: assetTypeKey
          ? modelDefaults?.[assetTypeKey]?.error_message
          : 'File type not supported.',
      });
      setFiles([]);
      return;
    }
    if (name.trim().includes(' ')) {
      setResponseAlert({
        isOpen: true,
        inputProps: { success: false },
        message: 'File name should not contain spaces. Please rename the file and try again.',
      });
      setFiles([]);
      
    } 
  };

  // find out key form assetTypes object where the value has a name as citations
  const assetTypeKey = Object.keys(assetTypes?.metadata_collection || {}).find(
    (key) => {
      const typedValue = assetTypes?.metadata_collection[key] as {
        name: string;
      }; // Explicitly cast value to the expected type
      return typedValue.name === asstType.label;
    }
  );

  // show the modal when the asset type is changed
  useEffect(() => {
    // Find the upload payload based on the asset type key
    const uploadPayload = assetTypeKey
      ? { ...assetTypes.metadata_collection[assetTypeKey] }
      : null; // Create a shallow copy of the payload
    setPrevSingleUploadPayload(singleUploadPayload); // Store the previous payload

    if (uploadPayload && asstType.value !== prevAssetType.current.value) {
      dispatch(
        singleUploadActions.updateSingleUploadPayload(
          JSON.parse(JSON.stringify(uploadPayload))
        )
      );
    }
    if (isInitialRender.current) {
      isInitialRender.current = false; // Skip the first render
      return;
    }
    // Show the modal only if the asset type value has changed
    if (
      prevAssetType.current.value &&
      asstType.value !== prevAssetType.current.value &&
      asstType.value !== ''
    ) {
      setAlert({
        isOpen: true,
        reason: 'assetTypeChange',
        message: `Any changes made to metadata will be lost if you change the asset type. Do you want to continue?`
      });
    } else {
      prevAssetType.current = asstType;
    }
  }, [assetTypeKey]);

  interface InputProps {
    reason?: string;
  }


  const handleActions = (action: string, inputProps?: InputProps) => {
    // scroll to the top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (action === 'YES') {
      // Handle the action when the user confirms
      setAlert({
        isOpen: false,
        reason: '',
        message: ''
      });
      setFiles([]); // Clear the files array
      dispatch(submitValidation({ eventType: '' }));
      dispatch(
        dropdownOptionsAction.updateDropdownOptions(
          DROPDOWN_OPTIONS_RESET,
          {},
          ''
        )
      );
      if (inputProps?.reason === 'assetTypeChange') {
        prevAssetType.current = asstType;
      }
      if (inputProps?.reason === 'clearData') {
        //
        setAssetType({
          value: '',
          label: ''
        });
        setActiveTab("Files"); // Reset the active tab to "Files"
        dispatch(singleUploadActions.clearSingleUploadPayload()); // Clear the single upload payload in the Redux store
      }
    } else {
      // Handle the action when the user cancels
      if (inputProps?.reason === 'assetTypeChange') {
        dispatch(
          singleUploadActions.updateSingleUploadPayload(prevSingleUploadPayload)
        ); // Restore the previous payload
        setAssetType(prevAssetType.current);
      }
      setAlert({
        isOpen: false,
        reason: '',
        message: ''
      });
    }
  };

  const handleClearData = () => {
    setAlert({
      isOpen: true,
      reason: 'clearData',
      message:
        'This will reset the metadata values to default. Do you want to continue?'
    });
  };

  const handlecheck = (event: any) => {
    setActiveTab(event.target.label);
  };

  const getDefaults = (metadataCollection: any) => {
    const defaults = metadataCollection?.DEFAULTS || {};
    let assetTypeDefaults = defaults?.[assetTypeKey || ''] || {};
    return assetTypeDefaults;
  };
  const shouldShowElement = (metadataElement: MetadataElement): boolean => {
    if (metadataElement.is_displayable !== 'Y') {
      return false;
    }
    if (metadataElement.is_editable === 'Y') {
      return true;
    }
    const defaults = getDefaults(assetTypes?.metadata_collection);
    return metadataElement.is_editable === 'N' && !!defaults?.[metadataElement.field_id];
  };

  const formattedSingleUploadPayload = () => {
    const id = singleUploadPayload?.metadata_collection?.metadata_element_list
      .find((el: any) => el.name === 'Citation Identifiers')
      ?.metadata_element_list.find(
        (el: any) => el.field_id === 'TFS.FIELD.PMID'
      )?.value?.id;

    const defaults = assetTypes?.metadata_collection?.MODEL_DEFAULTS || {};
    const assetNameFormat = assetTypeKey ? defaults[assetTypeKey]?.asset_name_logic : '';
    // Dynamically replace placeholders in asset_name_logic with corresponding values
    const getFormattedName = (logic: string) => {
      return logic.replace(/{(.*?)}/g, (_, fieldId) => {
        const fieldValue = singleUploadPayload?.metadata_collection?.metadata_element_list
          .flatMap((field: any) => field.metadata_element_list)
          .find((item: any) => item.field_id === fieldId)?.value?.id;

        return fieldValue || ''; // Replace with the field value or an empty string if not found
      });
    };
    const name = files[0]?.name || (getFormattedName(assetNameFormat) || singleUploadPayload?.name);


    // const name = files[0]?.name || (id ? `PMID - ${id}` : singleUploadPayload?.name);
    // return modified singleUploadPayload with file name as the first name and last name
    // Modify the singleUploadPayload to update the element with prompt "Name"
    if (singleUploadPayload?.metadata_collection?.metadata_element_list) {
      singleUploadPayload.metadata_collection.metadata_element_list.forEach(
        (field: any) => {
          field.metadata_element_list.forEach((item: any) => {
            if (item.prompt === 'Name') {
              item.value = { id: name, display_value: name }; // Update the value
            }
            if(item.prompt === 'File Status'){
              item.value = { id: '1', display_value: 'New' }; // Update the value to New
            }
          });
        }
      );
    }

    const payload = JSON.parse(JSON.stringify(singleUploadPayload)); // Create a deep copy of the payload
    return payload;

  };
  
  const getRequiredFields = () => {
    const mandatoryFields = singleUploadPayload?.metadata_collection?.metadata_element_list.filter(
      (field: any) =>
        field.metadata_element_list.some(
          (item: any) => shouldShowElement(item) && item.is_required === 'Y'
        )
    );
    const requiredFields = mandatoryFields?.flatMap((field: any) =>
      field.metadata_element_list.filter((item: any) => {
        if (item?.value && !item.value.id?.trim() && item.is_required === 'Y') {
          return true;
        }
        return item?.values?.every((value: any) => value.id === '' && item.is_required === 'Y');
      })
    );
    return requiredFields || [];
  };

  const scrollToFirstRequiredField = (requiredFields:any) => {
    if (requiredFields.length > 0) {
      // scroll to the first required field
      const firstRequiredField = document.getElementById(
        requiredFields[0].field_id
      );
      if (firstRequiredField) {
        window.scrollTo({
          top: firstRequiredField.offsetTop - 120,
          behavior: 'smooth'
        });
      }
    }
  };

  useEffect(() => {
    if(activeTab === 'Metadata') {
      // Scroll to the first required field when the Metadata tab is active
      scrollToFirstRequiredField(getRequiredFields());
    }
  }, [activeTab]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const requiredFields = getRequiredFields();
    if(requiredFields.length > 0) {
      if(activeTab !== 'Metadata') {
        setActiveTab('Metadata'); // Switch to Metadata tab if there are required fields
      }
      else {
        scrollToFirstRequiredField(requiredFields); // Scroll to the first required field
      }
      dispatch(submitValidation({ eventType: 'validate' }));
    } else {
      dispatch(submitValidation({ eventType: '' }));
      try {
        //update the field with prompt Name to first name and last name
        const payloadModified = formattedSingleUploadPayload();
        // Assuming singleUploadPayload is the payload you want to submit
        dispatch({ type: 'LOADING SUCCESS', payload: true });
        const formData = new FormData();
        formData.append('payload', JSON.stringify(payloadModified));
        if (asstType.value !== 'Citations' && files.length > 0) {
          formData.append('file', files[0]?.fileReference || new Blob());
        }

        const response = await assetUploadService
          .uploadAssets(formData, userName,'')
          .then((resp) => {
            return resp.json();
          });
        dispatch({ type: 'LOADING SUCCESS', payload: false });
        if (response?.asset_id?.length > 0) {
          setResponseAlert({
            isOpen: true,
            inputProps: {success: true},
            message:
              response?.message ||
              `Asset uploaded successfully with ID: ${response.asset_id.join(
                ', '
              )}`
          });
        }
        if (response?.error || response?.detail) {
          setResponseAlert({
            isOpen: true,
            inputProps: {success: false},
            message:
              response?.message || response?.detail ||
              'An error occurred while uploading the asset.'
          });
        }
      } catch (uploadError: any) {
        console.error('Error during asset upload:', uploadError);
        dispatch({ type: 'LOADING SUCCESS', payload: false });
        setResponseAlert({
          isOpen: true,
          inputProps: {success: false},
          message:
            error?.message || 'An error occurred while uploading the asset.'
        });
      }
    }
  };

  const handleClose = () => {
    if(responseAlert?.inputProps?.success) {
      setFiles([]);
      setAssetType({
        value: '',
        label: ''
      });
      dispatch(singleUploadActions.clearSingleUploadPayload());
      dispatch(submitValidation({ eventType: '' }));
      navigate(HOME_PAGE);
    }
    setResponseAlert({
      isOpen: false,
      inputProps: { success: false },
      message: ''
    });
  };

  return (
    <>
      <div className="navigation-bar">
        <CoreNavigationbar variant="horizontal" style={{ width: '100%' }}>
          <CoreNavigationbarItem
            slot="nav-item"
            selected={activeTab === 'Files'}
            variant="horizontal"
            label="Files"
            href="#"
            target="_blank"
            onClick={handlecheck}
          />
          <CoreNavigationbarItem
            disabled={asstType.value === ''}
            slot="nav-item"
            selected={activeTab === 'Metadata'}
            variant="horizontal"
            label="Metadata"
            href="#"
            target="_blank"
            onClick={asstType.value === '' ? undefined : handlecheck}
          />
        </CoreNavigationbar>
      </div>

      {activeTab === "Files" && (
        <SingleUploadContentFiles asstType={asstType} setAssetType={setAssetType} uploadFile={uploadFile}
          files={files} setFiles={setFiles} assetTypeKey={assetTypeKey}/>
      )
      }
      {
        activeTab === "Metadata" &&
        (asstType.label && <SingleUploadContent assetTypeKey={assetTypeKey || ''} />)
      }
      <ConfirmationAlertBox
        cancelText="Cancel"
        isDisabled={false}
        okText="Yes"
        isModalOpen={alert.isOpen}
        inputProps={{ reason: alert.reason }}
        handleAction={handleActions}
        message={alert.message}
      />
      {responseAlert?.isOpen && (
        <AlertBox
          message={responseAlert.message}
          isModalOpen
          handleClose={handleClose}
        />
      )}
      <ActionButton
        isDisabled={!asstType.value}
        submitFile={handleSubmit}
        clearUploadData={handleClearData}
      />
    </>
  );
};

export default SingleUploadMain;
